'use client';

import { useFetchScratchcards, useGamesList } from '@shared/hooks';
import { ROUTES, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { useGlobalSheet } from '../../../global-sheets/context/GlobalSheetContext';
import { processOfferData, shouldShowOfferExpirationDate, shouldShowWinnings } from '../../helpers/offerHelpers';
import { useOfferBundleActionHandlers } from '../../hooks/useOfferBundleActionHandlers';
import type { OfferBundleDetails } from '../../types';
import { getStepCode, shouldShowLoginButtons } from '../../utils/offerUtils';
import { OfferContent } from '../OfferContent/OfferContent';
import { OfferHeader } from '../OfferHeader/OfferHeader';
import { OfferStickyPanelButtons } from '../OfferStickyPanelButtons/OfferStickyPanelButtons';

type SingleOfferProps = {
  isAuthenticated: boolean;
  data: OfferBundleDetails;
  refetchCallback?: () => Promise<void>;
} & Record<`data-track-${string}`, string | number | undefined>;

function SingleOffer({ isAuthenticated, data, refetchCallback, ...trackingAttributes }: SingleOfferProps) {
  const t = useTranslations();
  const currencyFormatter = useCurrencyFormatter();
  const { openDepositSheet } = useGlobalSheet();
  const { games } = useGamesList();
  const { scratchcards } = useFetchScratchcards({});

  const actionHandlers = useOfferBundleActionHandlers({ refetch: refetchCallback, openDepositSheet, scratchcards });
  const { steps, offerSteps, currentAction, currentButtonText, productIds, productUrl } = processOfferData(data, isAuthenticated, t, currencyFormatter, games, scratchcards);
  const showLoginButtons = shouldShowLoginButtons(isAuthenticated, data);
  const termsTitle = t('offers.page.terms.title');
  const firstOffer = data.offers && data.offers[0];
  const showWinning = firstOffer ? shouldShowWinnings(firstOffer, offerSteps, data.bundleStatus) : false;
  const winnings = firstOffer?.winnings;
  const offerType = firstOffer?.type;
  const offerValidFrom = data.offers?.[0]?.availableFrom ? new Date(data.offers[0].availableFrom) : undefined;

  const getCurrentActiveStepIndex = () => {
    const activeIndex = steps.findIndex(step => step.state === 'active');
    return activeIndex !== -1 ? activeIndex : 0;
  };

  const activeStepIndex = getCurrentActiveStepIndex();
  const activeStep = steps[activeStepIndex];

  const currentButtonDisabled = activeStep?.disabled || false;

  const actionCallback = useCallback(() => {
    let minimumAmountForBonus: number | undefined = undefined;

    if (currentAction === 'DEPOSIT') {
      // Find the DEPOSIT step and get its initialAmount.fixed_amount
      const depositStep = data.offers?.[0]?.steps?.find(step => step.action === 'DEPOSIT') || (data.step?.action === 'DEPOSIT' ? data.step : undefined);

      if (depositStep?.initialAmount?.fixed_amount) {
        minimumAmountForBonus = depositStep.initialAmount.fixed_amount;
      }
    }

    const handlerData = {
      bundleCode: data.bundleCode,
      offerCode: data.offers?.[0]?.code,
      productIds: productIds,
      productURL: productUrl,
      minimumAmountForBonus,
      redirectUrlOnInsufficientDeposit: ROUTES.HOME,
    };

    const handler = actionHandlers[currentAction as keyof typeof actionHandlers];
    handler(handlerData);
  }, [currentAction, data.bundleCode, data.offers, data.step, data.title, actionHandlers, productIds, productUrl]);

  const stepCode = getStepCode(currentAction, data.bundleCode, data.offers?.[0]?.code);
  const gamesNames = Array.isArray(productIds) ? productIds : undefined;

  const stickyPanelContent = (
    <OfferStickyPanelButtons
      shouldShowLoginButtons={showLoginButtons}
      offerValidFrom={offerValidFrom}
      onStepAction={actionCallback}
      buttonLoading={null}
      currentAction={currentAction}
      currentButtonText={currentButtonText}
      currentButtonDisabled={currentButtonDisabled}
      currentStep={activeStep?.label}
      stepCode={stepCode}
      errorCode={data.assignationFailureReason}
      gamesNames={gamesNames}
      recurrenceNextAvailableDate={data.recurrenceNextAvailableDate}
    />
  );

  const shouldShowExpiration = shouldShowOfferExpirationDate(data.bundleStatus, data.offers);
  const expirationDate = shouldShowExpiration ? data.expirationDate : null;

  return (
    <div data-testid="single-offer" {...trackingAttributes}>
      <OfferHeader title={data.title} expirationDate={expirationDate} bundleStatus={data.bundleStatus} images={data.images} />

      <OfferContent
        steps={steps}
        stickyPanelContent={stickyPanelContent}
        termsContent={data.termsAndConditions?.join('\n') || ''}
        termsTitle={termsTitle}
        bundleStatus={data.bundleStatus}
        winnings={winnings}
        showWinning={!!showWinning}
        namespace="offers.page.obs.offer"
        offerType={offerType}
      />
    </div>
  );
}

export { SingleOffer, type SingleOfferProps };
